import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { StaffSession } from '../shared/types'
import {
  bootstrapStaffSession,
  clearStaffSession,
  getStaffSession,
  hasStaffSessionHint,
  logoutStaffServerSession,
  setOwnerUnauthorizedHandler,
  setStaffSession,
} from '../shared/api/ownerClient'

interface OwnerAuthValue {
  session: StaffSession | null
  isAuthenticated: boolean
  /** True while the boot silent refresh is restoring a previous login — gate redirects on it. */
  initializing: boolean
  /** Replace the current session (e.g. after a profile edit returns a refreshed session). */
  updateSession: (s: StaffSession) => void
  logout: () => void
}

const OwnerAuthContext = createContext<OwnerAuthValue | null>(null)

/**
 * Holds the staff session (mirrored from the in-memory store, D-053) and the auth actions for
 * the owner console. On mount it runs one silent refresh so the login survives page reloads —
 * the access token lives only in memory; the long session is the httpOnly refresh cookie.
 */
export function OwnerAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<StaffSession | null>(() => getStaffSession())
  const [initializing, setInitializing] = useState<boolean>(() => getStaffSession() === null && hasStaffSessionHint())
  const queryClient = useQueryClient()

  // Boot: restore the login via the refresh cookie (no-op when nobody was signed in).
  useEffect(() => {
    let active = true
    void bootstrapStaffSession().then((restored) => {
      if (active) {
        if (restored) {
          setSessionState(restored)
        }
        setInitializing(false)
      }
    })
    return () => {
      active = false
    }
  }, [])

  // When a staff API call 401s and the silent refresh couldn't rescue it, the session is truly
  // gone — drop it here too so the route guard (RequireStaff) redirects to the sign-in page
  // instead of looping 401s on the board.
  useEffect(() => {
    setOwnerUnauthorizedHandler(() => {
      clearStaffSession()
      setSessionState(null)
      queryClient.removeQueries({ queryKey: ['board'] })
    })
    return () => setOwnerUnauthorizedHandler(null)
  }, [queryClient])

  const value = useMemo<OwnerAuthValue>(() => {
    const updateSession = (s: StaffSession) => {
      setStaffSession(s)
      setSessionState(s)
    }
    const logout = () => {
      // Revoke the server-side session line (refresh-token family + cookie); best-effort —
      // the local session is gone either way.
      void logoutStaffServerSession().catch(() => undefined)
      clearStaffSession()
      setSessionState(null)
      // Drop the board cache so the next staff member doesn't briefly see the previous shop's orders.
      queryClient.removeQueries({ queryKey: ['board'] })
    }
    return { session, isAuthenticated: !!session, initializing, updateSession, logout }
  }, [session, initializing, queryClient])

  return <OwnerAuthContext.Provider value={value}>{children}</OwnerAuthContext.Provider>
}

export function useOwnerAuth(): OwnerAuthValue {
  const ctx = useContext(OwnerAuthContext)
  if (!ctx) {
    throw new Error('useOwnerAuth must be used within an OwnerAuthProvider')
  }
  return ctx
}
